import React, { useEffect, useMemo, useState } from "react";
import { api, fmtNum } from "@/lib/api";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, MapPin, Ban } from "lucide-react";

// Farbcodierung: Ring = Tour, Füllung = Milchsorte
const TOUR_FARBEN = {
  T01: "#1d4ed8", // Blau – Sammeltour Nord
  T02: "#0891b2", // Cyan – Sammeltour Süd
};
const TOUR_FALLBACK = "#475569";

const SORTEN_FARBEN = ["#64748b", "#d97706", "#16a34a", "#84cc16", "#9333ea", "#e11d48"];

// Erzeugt ein DivIcon, das visuell einem CircleMarker entspricht (Kreis mit
// Farbring aus Tour-Farbe und Füllung aus Milchsorten-Farbe). DivIcons können
// im Gegensatz zu CircleMarkern in MarkerClusterGroup gebündelt werden.
const supplierDivIcon = (fillColor, ringColor, gesperrt) => {
  const size = 18;
  const border = gesperrt ? 4 : 3;
  const html = `<div style="
    width:${size}px;height:${size}px;border-radius:9999px;
    background:${fillColor};
    border:${border}px ${gesperrt ? "dashed" : "solid"} ${ringColor};
    box-shadow:0 1px 3px rgba(0,0,0,0.35);
    box-sizing:border-box;
  "></div>`;
  return L.divIcon({
    html, className: "", iconSize: [size, size], iconAnchor: [size / 2, size / 2],
  });
};

// Icon für Cluster-Gruppen: zeigt Anzahl der gruppierten Lieferanten.
// Farbe/Größe steigen mit der Cluster-Größe.
const createClusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  const size = count < 10 ? 30 : count < 50 ? 36 : count < 200 ? 42 : 50;
  const bg = count < 10 ? "#3b82f6" : count < 50 ? "#8b5cf6" : count < 200 ? "#f59e0b" : "#ef4444";
  return L.divIcon({
    html: `<div style="
      width:${size}px;height:${size}px;border-radius:9999px;
      background:${bg};color:#fff;
      display:flex;align-items:center;justify-content:center;
      font-weight:700;font-size:${size >= 42 ? 14 : 12}px;
      font-family:ui-sans-serif,system-ui,sans-serif;
      border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.35);
    ">${count}</div>`,
    className: "supplier-cluster",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

export default function LieferantenKartePage() {
  const [lieferanten, setLieferanten] = useState([]);
  const [sorten, setSorten] = useState([]);
  const [sperren, setSperren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tourFilter, setTourFilter] = useState("alle");
  const [sorteFilter, setSorteFilter] = useState("alle");

  useEffect(() => {
    Promise.all([
      api.get("/lieferanten"),
      api.get("/milchsorten"),
      api.get("/sperren", { params: { status: "aktiv" } }),
    ])
      .then(([lf, ms, sp]) => {
        setLieferanten(lf.data);
        setSorten(ms.data);
        setSperren(sp.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const sortenFarbe = useMemo(() => {
    const map = {};
    sorten.forEach((s, i) => { map[s.id] = SORTEN_FARBEN[i % SORTEN_FARBEN.length]; });
    return map;
  }, [sorten]);

  const sortenName = useMemo(() => {
    const map = {};
    sorten.forEach((s) => { map[s.id] = s.name; });
    return map;
  }, [sorten]);

  const gesperrt = useMemo(() => new Set(sperren.map((s) => s.liefernummer)), [sperren]);

  const touren = useMemo(
    () => [...new Set(lieferanten.map((l) => l.tour).filter(Boolean))].sort(),
    [lieferanten]
  );

  const marker = useMemo(
    () => lieferanten.filter((l) =>
      l.lat != null && l.lng != null &&
      (tourFilter === "alle" || l.tour === tourFilter) &&
      (sorteFilter === "alle" || l.milchsorte_id === sorteFilter)
    ),
    [lieferanten, tourFilter, sorteFilter]
  );

  const bounds = useMemo(() => {
    const pts = lieferanten.filter((l) => l.lat != null && l.lng != null).map((l) => [l.lat, l.lng]);
    return pts.length ? pts : [[47.80, 13.25], [48.17, 13.70]];
  }, [lieferanten]);

  if (loading)
    return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Lieferanten-Karte</h1>
          <p className="text-sm text-muted-foreground">
            {fmtNum(marker.length)} Standorte · Ring = Tour · Füllung = Milchsorte
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={tourFilter} onValueChange={setTourFilter}>
            <SelectTrigger data-testid="map-tour-filter" className="w-[140px]">
              <SelectValue placeholder="Tour" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">Alle Touren</SelectItem>
              {touren.map((t) => (
                <SelectItem key={t} value={t}>Tour {t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sorteFilter} onValueChange={setSorteFilter}>
            <SelectTrigger data-testid="map-milktype-filter" className="w-[170px]">
              <SelectValue placeholder="Milchsorte" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">Alle Milchsorten</SelectItem>
              {sorten.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div data-testid="supplier-map" className="relative h-[540px] w-full overflow-hidden rounded-xl z-0">
            <MapContainer
              bounds={bounds}
              boundsOptions={{ padding: [40, 40] }}
              scrollWheelZoom
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MarkerClusterGroup
                chunkedLoading
                iconCreateFunction={createClusterIcon}
                showCoverageOnHover={false}
                spiderfyOnMaxZoom
                disableClusteringAtZoom={14}
                maxClusterRadius={60}
              >
                {marker.map((l) => {
                  const ist_gesperrt = gesperrt.has(l.liefernummer);
                  const ringColor = ist_gesperrt ? "#dc2626" : (TOUR_FARBEN[l.tour] || TOUR_FALLBACK);
                  const fillColor = sortenFarbe[l.milchsorte_id] || "#94a3b8";
                  return (
                    <Marker
                      key={l.liefernummer}
                      position={[l.lat, l.lng]}
                      icon={supplierDivIcon(fillColor, ringColor, ist_gesperrt)}
                    >
                      <Popup>
                        <div style={{ minWidth: 200, fontFamily: "Inter, sans-serif" }}>
                          <p style={{ fontWeight: 600, margin: "0 0 2px" }}>
                            {l.liefernummer} · {l.name}
                          </p>
                          <p style={{ margin: 0, fontSize: 12, color: "#475569" }}>
                            {l.hofname} · {l.adresse}, {l.plz} {l.ort}
                          </p>
                          <p style={{ margin: "6px 0 0", fontSize: 12 }}>
                            <span style={{
                              display: "inline-block", width: 10, height: 10, borderRadius: 999,
                              background: sortenFarbe[l.milchsorte_id] || "#94a3b8", marginRight: 5,
                            }} />
                            {sortenName[l.milchsorte_id] || "–"}
                            <span style={{ margin: "0 6px", color: "#cbd5e1" }}>|</span>
                            <span style={{
                              display: "inline-block", width: 10, height: 10, borderRadius: 999,
                              border: `3px solid ${TOUR_FARBEN[l.tour] || TOUR_FALLBACK}`, marginRight: 5,
                            }} />
                            Tour {l.tour || "–"}
                          </p>
                          {ist_gesperrt && (
                            <p style={{ margin: "6px 0 0", fontSize: 12, color: "#dc2626", fontWeight: 600 }}>
                              Liefersperre aktiv
                            </p>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </MarkerClusterGroup>
            </MapContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Tour (Ring)</p>
            <div className="flex flex-wrap gap-3" data-testid="map-legend-tour">
              {touren.map((t) => (
                <div key={t} className="flex items-center gap-1.5 text-sm">
                  <span className="inline-block h-3.5 w-3.5 rounded-full border-[3px]"
                    style={{ borderColor: TOUR_FARBEN[t] || TOUR_FALLBACK }} />
                  Tour {t}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Milchsorte (Füllung)</p>
            <div className="flex flex-wrap gap-3" data-testid="map-legend-milktype">
              {sorten.map((s) => (
                <div key={s.id} className="flex items-center gap-1.5 text-sm">
                  <span className="inline-block h-3.5 w-3.5 rounded-full"
                    style={{ background: sortenFarbe[s.id] }} />
                  {s.name}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Status</p>
            <div className="flex items-center gap-1.5 text-sm" data-testid="map-legend-status">
              <Ban className="h-4 w-4 text-destructive" />
              Roter, strichlierter Ring = Liefersperre aktiv
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
